(function () {
  'use strict';

  class SoArtSlideshow {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-soart-track]');
      this.slides = Array.from(root.querySelectorAll('[data-soart-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-soart-dot]'));
      this.prevBtn = root.querySelector('[data-soart-prev]');
      this.nextBtn = root.querySelector('[data-soart-next]');
      this.viewport = root.querySelector('[data-soart-viewport]');

      this.index = 0;
      this.count = this.slides.length;
      this.autoplay = root.dataset.autoplay === 'true';
      this.pauseHover = root.dataset.pauseHover === 'true';
      this.interval = parseInt(getComputedStyle(root).getPropertyValue('--soart-interval')) || 6000;
      this.timer = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.count === 0) return;
      this.init();
    }

    init() {
      this.setActive(0, true);

      // Enable transitions after first paint (respects "show initial transition")
      const showInitial = this.root.dataset.initialTransition === 'true';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.root.classList.add('soart-ready');
          if (showInitial && !this.reduceMotion && this.count > 1) {
            this.goTo(1);
            setTimeout(() => this.startAutoplay(), this.interval);
          } else {
            this.startAutoplay();
          }
        });
      });

      if (this.count < 2) return;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.userNav(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.userNav(this.index + 1));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.userNav(parseInt(dot.dataset.soartDot, 10)));
      });

      // Keyboard
      this.root.setAttribute('tabindex', '-1');
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { this.userNav(this.index - 1); }
        else if (e.key === 'ArrowRight') { this.userNav(this.index + 1); }
      });

      // Hover pause (desktop)
      if (this.pauseHover) {
        this.root.addEventListener('mouseenter', () => this.stopAutoplay());
        this.root.addEventListener('mouseleave', () => this.startAutoplay());
      }

      // Pause when tab hidden
      this._visHandler = () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      };
      document.addEventListener('visibilitychange', this._visHandler);

      // Touch / swipe
      this.bindSwipe();
    }

    setActive(newIndex, immediate) {
      const prev = this.index;
      this.slides.forEach((slide, i) => {
        slide.classList.remove('is-active', 'is-prev');
        slide.setAttribute('aria-hidden', i === newIndex ? 'false' : 'true');
      });
      if (this.slides[prev] && prev !== newIndex) this.slides[prev].classList.add('is-prev');
      this.slides[newIndex].classList.add('is-active');

      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-selected', i === newIndex ? 'true' : 'false');
      });
      this.index = newIndex;
    }

    goTo(target) {
      let next = (target + this.count) % this.count;
      this.setActive(next);
    }

    userNav(target) {
      this.goTo(target);
      this.restartAutoplay();
    }

    startAutoplay() {
      if (!this.autoplay || this.count < 2 || document.hidden) return;
      this.stopAutoplay();
      this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
    }

    stopAutoplay() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    bindSwipe() {
      let startX = 0, startY = 0, tracking = false;
      const threshold = 45;
      this.viewport.addEventListener('touchstart', (e) => {
        tracking = true;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      this.viewport.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) this.userNav(this.index + 1);
          else this.userNav(this.index - 1);
        }
      }, { passive: true });
    }

    destroy() {
      this.stopAutoplay();
      if (this._visHandler) document.removeEventListener('visibilitychange', this._visHandler);
    }
  }

  const instances = new WeakMap();

  function initAll(scope) {
    const roots = (scope || document).querySelectorAll('[data-soart-slideshow]');
    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new SoArtSlideshow(root));
    });
  }

  function destroy(root) {
    const inst = instances.get(root);
    if (inst) { inst.destroy(); instances.delete(root); }
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', () => initAll());

  // Shopify Theme Editor lifecycle
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-soart-slideshow]');
    if (root) destroy(root);
  });
  document.addEventListener('shopify:section:select', (e) => {
    const root = e.target.querySelector('[data-soart-slideshow]');
    const inst = root && instances.get(root);
    if (inst) inst.stopAutoplay();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const root = e.target.querySelector('[data-soart-slideshow]');
    const inst = root && instances.get(root);
    if (inst) inst.startAutoplay();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target.closest('[data-soart-slideshow]');
    const inst = root && instances.get(root);
    const slide = e.target.closest('[data-soart-slide]');
    if (inst && slide) {
      inst.stopAutoplay();
      inst.goTo(parseInt(slide.dataset.soartIndex, 10));
    }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const root = e.target.closest('[data-soart-slideshow]');
    const inst = root && instances.get(root);
    if (inst) inst.startAutoplay();
  });
})();